import * as React from "react";
import { useContext, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UserContext } from '../../functions/Auth';
import { uploadMedia } from '../../api/storages';
import { getCurrentDateTime, validateMedia } from '../../utils';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import blue from '@mui/material/colors/blue';

// icons
import FileUploadIcon from '@mui/icons-material/FileUpload';

// type
type DropzoneProps = {
    storageUsed: number,
    storageTotal: number,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    setFileAdditionCount: (cnt: number) => void;
    onFileAddition: (file: File, uploadedDate: string) => void;
    onFileUploaded: (file: File, url: string, id: number) => void;
}

function Dropzone(props: DropzoneProps) {
    const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
    const { storageUsed, storageTotal, setErrorMessage, setFileAdditionCount, onFileAddition, onFileUploaded }  = props;
    const userInfo = useContext(UserContext);
    const userId = userInfo?.userId;

    // ドロップまたはファイル選択
    const onDrop = useCallback((acceptedFiles: File[]) => {
        console.log(acceptedFiles);

        // メディアのアップロード
        // ファイル、ルームID、ユーザーIDが存在するか確認
        if (!acceptedFiles || !userId) {
            return false;
        }

        // バリデーション
        const newFilesSize = acceptedFiles.reduce((acc, file) => acc + file.size, 0);
        const uploadingSize = uploadingFiles.reduce((acc, file) => acc + file.size, 0);
        const totalSize = storageUsed + newFilesSize + uploadingSize;
        if (totalSize > storageTotal) {
            setErrorMessage(`使用ファイル量が制限を超えるためアップロードできません。不要なファイルを削除してください。`);
            return;
        }

        const hasInvalidFile = acceptedFiles.some((file) => {
            const { isValid, errorMessage } = validateMedia(file);
            if (!isValid && errorMessage) {
                setErrorMessage(errorMessage);
                return true;  // 一つでも無効なファイルがあれば中断
            }
            if (isValid) {
                setErrorMessage('');
            }
            return false;
        });

        if (hasInvalidFile) {
            return;
        }

        // アップロード中のファイルを更新
        setUploadingFiles([...uploadingFiles, ...acceptedFiles]);

        // ファイルアップロード
        setFileAdditionCount(acceptedFiles.length);
        acceptedFiles.forEach((file) => {
            onFileAddition(file, getCurrentDateTime());
            uploadMedia(file, userId.toString())
                .then((result) => {
                    // アップロードしたメディアを表示する処理
                    onFileUploaded(file, result.url, result.id);
                    // アップロードが完了したファイルを setUploadingFiles から削除
                    setUploadingFiles((prevUploadingFiles) => 
                        prevUploadingFiles.filter((prevUploadingFile) => prevUploadingFile !== file)
                    );
                })
                .catch((error) => {
                    console.log('エラー', error)
                })
        });
    }, [userId, uploadingFiles, storageUsed, storageTotal, setFileAdditionCount, setErrorMessage, onFileAddition, onFileUploaded])
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    return (
        <Box
            sx={{
                maxWidth: 768,
                mx: "auto",
            }}
        >
            <Box
                {...getRootProps()}
                sx={{
                    p: { mb: 2, tb: 4 },
                    backgroundColor: isDragActive ? blue[50] : (theme) => theme.palette.grey[100],
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: (theme) => theme.palette.grey[700],
                    borderRadius: 4,
                }}
            >
                <input {...getInputProps()} />
                <Typography
                    component="p"
                    variant="h5"
                    fontWeight="700"
                    gutterBottom
                    align="center"
                >
                    ファイルをドロップしてアップロード
                </Typography>
                <FileUploadIcon sx={{
                    display: "block",
                    mx: "auto",
                    mb: 2,
                    fontSize: 80,
                    color: (theme) => theme.palette.grey[500],
                }} />
                <Button
                    variant="outlined"
                    color="inherit"
                    component="div"
                    sx={{
                        position: "relative",
                        display: "inline-block",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: (theme) => theme.palette.common.white,
                    }}
                >ファイルを選択</Button>
            </Box>
            <Box
                sx={{
                    mt: 1
                }}
            >
                <Typography component="p" variant="caption">
                    画像　対応ファイル：JPEG、PNG、GIF<br />
                    動画　対応ファイル：MP4<br />
                    最大２Gまで利用可能 <br />
                </Typography>
            </Box>
        </Box>
    )
}
export default Dropzone;
